/**
 * Defines categories of allowed elements.
 */
export interface UseProfilesConfig {
  /**
   * Allow all safe MathML elements.
   */
  mathMl?: boolean | undefined;

  /**
   * Allow all safe SVG elements.
   */
  svg?: boolean | undefined;

  /**
   * Allow all save SVG Filters.
   */
  svgFilters?: boolean | undefined;

  /**
   * Allow all safe HTML elements.
   */
  html?: boolean | undefined;
}

/**
 * Configuration options for DOMPurify.sanitize that prioritize security
 * and are less likely to introduce XSS vulnerabilities.
 * Refer to the DOMPurify documentation for more details: https://github.com/cure53/DOMPurify/blob/main/src/config.ts
 */
export interface ConfigOptions {
  /**
   * Allow ARIA attributes, leave other safe HTML as is (default is true).
   */
  ALLOW_ARIA_ATTR?: boolean | undefined;
  /**
   * Allow HTML5 data attributes, leave other safe HTML as is (default is true).
   */
  ALLOW_DATA_ATTR?: boolean | undefined;
  /**
   * Glue elements like style, script or others to `document.body` and prevent unintuitive browser behavior in several edge-cases (default is false).
   */
  FORCE_BODY?: boolean | undefined;
  /**
   * Sanitize a node "in place", which is much faster depending on how you use DOMPurify.
   */
  IN_PLACE?: boolean | undefined;
  /**
   * Strip `{{ ... }}`, `${ ... }` and `<% ... %>` to make output safe for template systems.
   * Be careful please, this mode is not recommended for production usage.
   * Allowing template parsing in user-controlled HTML is not advised at all.
   * Only use this mode if there is really no alternative.
   */
  SAFE_FOR_TEMPLATES?: boolean | undefined;
  /**
   * Use DOM Clobbering protection on output (default is true, handle with care, minor XSS risks here).
   */
  SANITIZE_DOM?: boolean | undefined;
  /**
   * Enforce strict DOM Clobbering protection via namespace isolation (default is false).
   * When enabled, isolates the namespace of named properties (i.e., `id` and `name` attributes)
   * from JS variables by prefixing them with the string `user-content-`
   */
  SANITIZE_NAMED_PROPS?: boolean | undefined;
  /**
   * Controls categories of allowed elements.
   * [IMPORTANT]: If a category is set to true, it will process only that category.
   */
  USE_PROFILES?: false | UseProfilesConfig | undefined;
  /**
   * Return entire document including <html> tags (default is false).
   */
  WHOLE_DOCUMENT?: boolean | undefined;
  /**
   * Change the parser type so sanitized data is treated as XML and not as HTML, which is the default.
   */
  PARSER_MEDIA_TYPE?: DOMParserSupportedType;
}

export interface Hooks {
  beforeSanitizeElements: Function;
  afterSanitizeElements: Function;
  beforeSanitizeAttributes: Function;
  afterSanitizeAttributes: Function;
  beforeSanitizeShadowDOM: Function;
  afterSanitizeShadowDOM: Function;
  uponSanitizeElement: Function;
  uponSanitizeAttribute: Function;
  uponSanitizeShadowNode: Function;
}

/**
 * Items removed by the sanitizedHTMLAndValidate function can be: attributes or tags.
 */
export interface RemovedItem {
  element?: any;
  attribute?: any;
}

/**
 * This is the default configuration for DOMPurify to be used with this policy.
 */
export const defaultConfig = {
  ADD_TAGS: ["#comment"], // Only this tag will be allowed by default, since it's not identified by domPurify as a tag after removal.
  RETURN_TRUSTED_TYPE: false,
};

/*
DOMPurify implicitly remove tags and attributes that are not allowed by default.
There are known risky tags and attributes defined here:
https://github.com/cure53/DOMPurify/wiki/Default-TAGs-ATTRIBUTEs-allow-list-&-blocklist [list may be incomplete]
*/
export const htmlBlockList = [
  "applet",
  "base",
  "basefont",
  "command",
  "embed",
  "frame",
  "frameset",
  "keygen",
  "link",
  "meta",
  "noframes",
  "noscript",
  "object",
  "param",
  "script",
  "title",
];

export const htmlBlockListAttributes = [
  "contenteditable",
  "contextmenu",
  "controls",
  "data",
  "decoding",
  "defer",
  "dirname",
  "draggable",
  "dropzone",
  "form",
  "formaction",
  "http-equiv",
  "icon",
  "importance",
  "itemprop",
  "keytype",
  "kind",
  "language",
  "lazyload",
  "manifest",
  "minlength",
  "muted",
  "ping",
  "scoped",
  "slot",
  "spellcheck",
  "srcdoc",
  "srclang",
  "start",
  "target",
  "translate",
  "wrap",
  "onerror",
  "onclick",
  "onload",
  "onmouseover",
  "onfocus",
  "onblur",
  "src",
  "href",
];

export const testContent = {
  maliciousHTML: "<div onerror=\"alert('XSS')\"></div>",
  maliciousSVG:
    '<svg><circle r="40" cy="50" cx="50" onclick="alert(\'XSS\')"></circle></svg>',
  maliciousMathML:
    "<math><msup><mi>x</mi><mn>2</mn></msup><div onclick=\"alert('XSS')\"></div></math>",
  sanitizedHTML: "<div></div>",
  sanitizedSVG: '<svg><circle r="40" cy="50" cx="50"></circle></svg>',
  sanitizedMathML: "<math><msup><mi>x</mi><mn>2</mn></msup></math><div></div>",
  missingAttrHTML: ['<div paraid="example"></div>', '<div lang="en"></div>'],
  missingTagsHTML: [
    '<div class="InsertMediaM365Dialog" id="insertmediadialog"> <div class="InsertMediaM365Panel" id="insertmediapanel"> <iframe id="insertmediaframe" class="InsertMediaFrame" name="InsertMediaFrame"></iframe></div></div>',
    `<!-- This is a comment --><div>Content</div>`,
  ],
};
